import React, { forwardRef, Fragment, useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Spacings from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import Paragraph from '@polestar/component-warehouse-react/components/paragraph/Paragraph';
import Icon from '@polestar/component-warehouse-react/components/icon/Icon';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import { createCustomFontSize } from '@polestar/component-warehouse-react/tokens/font-sizes/FontSizes';
import Skeleton from '@polestar/component-warehouse-react/components/skeleton/Skeleton';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import { m as motion } from 'framer-motion';
import { createTransition } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import { useIsSSR } from '@polestar/component-warehouse-react/contexts/ssr';
import warnOnce from '@polestar/component-warehouse-react/helpers/warnOnce';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
var ImageLoadingState;
(function (ImageLoadingState) {
    ImageLoadingState["loading"] = "loading";
    ImageLoadingState["loaded"] = "loaded";
    ImageLoadingState["failed"] = "failed";
})(ImageLoadingState || (ImageLoadingState = {}));
// @ts-ignore
const Image = forwardRef(({ alt, src, objectFit, objectPosition, stretch, errorMessage = '', showLoadingState = true, fadeIn = false, style = {}, onLoad, onLoaded, onError, ...props }, ref) => {
    const source = src;
    const alternative = alt;
    if (!alternative && alternative !== '' && props.role !== 'presentation') {
        warnOnce('no-alt-attribute-on-image', '[Image] `alt` or `alternative` prop missing');
    }
    if (!source || source === '') {
        warnOnce('no-src-attribute-on-image', '[Image] `src` or `source` prop missing');
    }
    const [failedLoading, setFailedLoading] = useState({});
    const [imageLoadingState, setImageLoadingState] = useState(showLoadingState ? ImageLoadingState.loading : ImageLoadingState.loaded);
    const [currentSource, setCurrentSource] = useState(source);
    const [cached, setCached] = useState();
    const isLoading = imageLoadingState === ImageLoadingState.loading;
    const isSSR = useIsSSR();
    const isVisible = 
    // loading=lazy expects that this component is visible otherwise it will
    // not trigger a load so `display:none` or a negative margin will prevent
    // it from loading the image (Skeleton stays visible which is wrong)
    isLoading && !isSSR ? { visibility: 'hidden', width: 0, height: 0 } : {};
    const imageRef = useCallback((node) => {
        if (node) {
            // validate when a ref is defined if the image is already loaded
            // onLoad doesn't always trigger when inside <picture> when it is already loaded
            if (node.complete && imageLoadingState !== ImageLoadingState.loaded) {
                setImageLoadingState(ImageLoadingState.loaded);
            }
            if (node.complete) {
                // trigger a new render to ensure the loaded event is triggered
                setCached(node.getAttribute('src'));
            }
        }
    }, [imageLoadingState]);
    const combinedRef = useCombinedRef(ref, imageRef);
    useEffect(() => {
        // check if loading failed
        if (imageLoadingState === ImageLoadingState.failed && !failedLoading[source ? source : '']) {
            setImageLoadingState(ImageLoadingState.loading);
        }
    }, [source, imageLoadingState, failedLoading]);
    useEffect(() => {
        if (source !== currentSource) {
            // cleanup failed states
            delete failedLoading[currentSource ? currentSource : ''];
            setFailedLoading({ ...failedLoading });
            // reset loading state while a new source is provided
            setCurrentSource(source);
            if (cached !== source) {
                setImageLoadingState(ImageLoadingState.loading);
            }
            // reset to discover new sources when they are already cached
            setCached(undefined);
        }
    }, [source, currentSource, failedLoading, cached]);
    // do not cache this because of issues with Safari
    const handleLoad = (e) => {
        setImageLoadingState(ImageLoadingState.loaded);
        onLoad === null || onLoad === void 0 ? void 0 : onLoad(e);
    };
    useEffect(() => {
        if (imageLoadingState === ImageLoadingState.loaded) {
            onLoaded === null || onLoaded === void 0 ? void 0 : onLoaded();
        }
    }, [imageLoadingState, onLoaded]);
    const handleError = useCallback((e) => {
        setFailedLoading({ ...failedLoading, [source ? source : '']: true });
        setImageLoadingState(ImageLoadingState.failed);
        onError === null || onError === void 0 ? void 0 : onError(e);
    }, [source, failedLoading, onError]);
    const { width, height, ...errorProps } = props;
    const widthValue = typeof width === 'number' ? `${width}px` : width;
    const heightValue = typeof height === 'number' ? `${height}px` : height;
    if (imageLoadingState === ImageLoadingState.failed) {
        return (React.createElement(StyledImageError, { ...errorProps, 
            // @ts-ignore
            style: style, width: widthValue, height: heightValue, ref: ref, stretch: stretch || false },
            React.createElement(Icon, { icon: Icons.questionCircle, size: createCustomFontSize(24 / 16) }),
            React.createElement(StyledImageErrorMessage, null, errorMessage)));
    }
    return (React.createElement(Fragment, null,
        React.createElement(StyledImage, { draggable: false, ...props, ref: combinedRef, style: { ...style, ...isVisible }, initial: false, animate: {
                opacity: !fadeIn || !isLoading || isSSR ? 1 : 0,
            }, transition: createTransition({ disabled: !fadeIn }), key: source, src: source, alt: alternative || '', objectFit: objectFit, objectPosition: objectPosition, stretch: stretch, loadingState: imageLoadingState, showLoadingState: showLoadingState, onLoad: handleLoad, onError: handleError }),
        isLoading && showLoadingState && !isSSR && (React.createElement(Skeleton
        // @ts-ignore
        , { 
            // @ts-ignore
            style: { ...style }, width: widthValue, height: heightValue }))));
});
Image.displayName = 'Image';
export default Image;
const StyledImage = styled(motion.img, shouldNotForwardProps(false, 'loadingState', 'objectFit', 'objectPosition', 'stretch', 'width', 'height', 'showLoadingState'))(({ objectPosition, objectFit, width, height, stretch }) => css(css `
        display: block;
        max-width: 100%;
        width: ${typeof width === 'number' ? `${width}px` : width};
        height: ${typeof height === 'number' ? `${height}px` : height};
        text-align: center;
        position: relative;
      `, objectFit &&
    css `
          object-fit: ${objectFit};
        `, objectPosition &&
    css(typeof objectPosition === 'string'
        ? css `
                object-position: ${objectPosition};
              `
        : css `
                ${responsiveStyles({ objectPosition })};
              `), stretch &&
    css `
          display: flex;
          width: 100%;
          height: 100%;
          flex: 1;
        `));
const StyledImageError = styled('div', shouldNotForwardProps(false, 'width', 'height', 'stretch')) `
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.image.backgroundColor};
  flex-direction: column;
  color: ${({ theme }) => theme.image.color};
  padding: ${Spacings.medium};
  width: ${(props) => (props.stretch ? '100%' : props.width ? props.width : 'auto')};
  height: ${(props) => (props.stretch ? '100%' : props.height ? props.height : 'auto')};
`;
const StyledImageErrorMessage = styled(Paragraph) `
  margin-block-start: ${Spacings.small};
  color: ${({ theme }) => theme.image.color};
`;
